import { useQuery } from '@apollo/client';
import { Delta } from 'quill/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import calculateVersionChanges from './calculateVersionChanges';
import * as styles from './NewPostHistoryView.module.scss';
import NewVersionChangesWall from './NewVersionChangesWall';
import partitionAndGroupHistories from './partitionAndGroupHistories';
import VersionChanges from './VersionChanges';
import VersionChangesDropdown from './VersionChangesDropdown';
import Page from '../../app/layout/Page';
import Timestamp from '../../components/Timestamp';
import { AssetTokenProvider } from '../../editor/AssetTokenContext';
import AttributeHandler from '../../editor/AttributeHandler';
import type {
  MagneticWallFocus,
  MagneticWallHighlight,
} from '../../editor/editorSlice';
import QuillContext from '../../editor/QuillContext';
import useSyncMagneticWallStates from '../../editor/useSyncMagneticWallState';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import InternalTopbar from '../../topbar/InternalTopbar';
import ignoreInChromatic from '../../utils/ignoreInChromatic';
import PageMeta from '../../utils/PageMeta';
import ControlBarContributorsButton from '../ControlBarContributorsButton';
import { scrollMagneticWallItemIntoView } from '../MagneticWallItem';
import PostLayout from '../PostLayout';
import PostLayoutProvider from '../PostLayoutProvider';
import parseMagneticWallHash from '../utils/parseMagneticWallHash';

const NEW_POST_HISTORY_VIEW_QUERY = gql(/** @complexity 24 */ `
    query NewPostHistoryView($id: ID!, $versionFrom: Int!, $versionTo: Int!) {
      post(id: $id) {
        id
        title
        histories {
          id
          ...partitionAndGroupHistories_history
        }
        history(versionFrom: $versionFrom, versionTo: $versionTo) {
          id
          snapshot
          diff
          insertedAt
          ...NewVersionChangesWall_history
        }
        publishedAt
        archivedAt
        ...VersionChangesDropdown_post
        ...ControlBarContributorsButton_post
      }
      entityAssetToken(entityId: $id, entityType: POST) {
        ...AssetTokenProvider_entityAssetToken
      }
    }
  `);

const getSelectedVersionRange = (
  histories: DocumentType<
    typeof NEW_POST_HISTORY_VIEW_QUERY
  >['post']['histories'],
  versionFrom: number,
  versionTo: number,
) => {
  const { accessibleHistories } = partitionAndGroupHistories(histories ?? []);
  const minHistory = accessibleHistories.find(
    (history) => versionFrom >= history.versionFrom,
  );

  const maxHistory =
    accessibleHistories
      .toReversed()
      .find((history) => versionTo <= history.versionTo) ??
    accessibleHistories.at(0);

  if (!maxHistory) {
    return null;
  }

  return {
    versionFrom:
      minHistory?.versionFrom != null
        ? minHistory.versionFrom
        : maxHistory.versionFrom,
    versionTo: maxHistory.versionTo,
  };
};

interface NewPostHistoryViewProps {
  postId: string;
  from: string;
  to: string;
}

const InnerNewPostHistoryView = ({
  postId,
  from,
  to,
}: NewPostHistoryViewProps) => {
  const [, setSearchParams] = useSearchParams();
  const versionFrom = parseInt(from, 10);
  const versionTo = parseInt(to, 10);

  const { data } = useQuery(NEW_POST_HISTORY_VIEW_QUERY, {
    variables: {
      id: postId,
      versionFrom,
      versionTo,
    },
  });

  const post = data?.post;

  const versionRange = getSelectedVersionRange(
    post?.histories ?? [],
    versionFrom,
    versionTo,
  );

  useEffect(() => {
    if (versionRange?.versionFrom != null && versionRange?.versionTo != null) {
      if (
        versionFrom === versionRange.versionFrom &&
        versionTo === versionRange.versionTo
      ) {
        return;
      }

      setSearchParams(
        {
          histories: `${versionRange.versionFrom}-${versionRange.versionTo}`,
        },
        { replace: true },
      );
    }
  }, [
    setSearchParams,
    versionRange?.versionFrom,
    versionRange?.versionTo,
    versionFrom,
    versionTo,
  ]);

  // We have this in a separate useMemo to avoid recalculating the version changes
  // when history changes but the diff object is the same. This is for example
  // the case when the user unresolves a thread.
  const calculated = useMemo(
    () =>
      post?.history.diff ? calculateVersionChanges(post.history.diff) : null,
    [post?.history.diff],
  );

  const { content, allVersionChanges } = useMemo(() => {
    if (!post?.history.diff || !calculated) {
      return { content: undefined, allVersionChanges: undefined };
    }

    const composed = new Delta(post?.history.snapshot)
      .compose(new Delta(post?.history.diff))
      .compose(calculated.delta ?? new Delta());

    return {
      content: composed,
      allVersionChanges: calculated.versionChanges,
    };
  }, [calculated, post?.history.snapshot, post?.history.diff]);

  const [magneticWallHighlight, setMagneticWallHighlight] =
    useState<MagneticWallHighlight | null>(null);
  const [magneticWallFocus, setMagneticWallFocus] =
    useState<MagneticWallFocus | null>(null);

  useSyncMagneticWallStates({ magneticWallFocus, magneticWallHighlight });

  const { hash } = useLocation();
  useEffect(() => {
    const parseResult = parseMagneticWallHash(hash);
    if (!parseResult) return;

    setMagneticWallFocus({
      active: true,
      id: parseResult.id,
      type: parseResult.type,
      hasExpandIntent: false,
    });

    scrollMagneticWallItemIntoView(parseResult.id);
  }, [hash]);

  if (!post || !versionRange) {
    return null;
  }

  const postTitle = post.title;
  const initialTitle = 'History - Slab';

  return (
    <VersionChanges.Root
      allVersionChanges={allVersionChanges}
      onFocusChange={() => setMagneticWallFocus(null)}
      hideFocusedDiff={magneticWallFocus != null}
    >
      <PageMeta
        title={postTitle ? `${postTitle} - ${initialTitle}` : initialTitle}
      />
      <PostLayoutProvider
        expandableAreas={{ sidebar: true, toc: false, magneticWall: true }}
      >
        <PostLayout.Root
          topbar={
            <VersionChanges.PostLayoutTopbarContainer>
              <VersionChanges.Topbar>
                <VersionChangesDropdown.Root>
                  <VersionChangesDropdown.TriggerButton>
                    Version {versionRange.versionTo}
                  </VersionChangesDropdown.TriggerButton>
                  <VersionChangesDropdown.Content post={post} />
                </VersionChangesDropdown.Root>
              </VersionChanges.Topbar>
            </VersionChanges.PostLayoutTopbarContainer>
          }
        >
          <PostLayout.ControlBar>
            <div className={styles.controlBar}>
              <ControlBarContributorsButton post={post} interactionsDisabled>
                Edited{' '}
                <Timestamp
                  date={post.history.insertedAt}
                  {...ignoreInChromatic()}
                />
              </ControlBarContributorsButton>
            </div>
          </PostLayout.ControlBar>
          <PostLayout.Above />
          <PostLayout.TOC />
          <PostLayout.Editor>
            <AttributeHandler
              onAttributeClick={(attribute, type) => {
                setMagneticWallFocus({
                  active: true,
                  id: attribute.id,
                  type,
                  hasExpandIntent: false,
                });
              }}
            >
              <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
                <VersionChanges.Post
                  key={`${versionFrom}-${versionTo}`}
                  content={content}
                />
              </AssetTokenProvider>
            </AttributeHandler>
          </PostLayout.Editor>
          <PostLayout.MagneticWall>
            <NewVersionChangesWall
              history={post.history}
              setMagneticWallFocus={setMagneticWallFocus}
              magneticWallFocus={magneticWallFocus}
              setMagneticWallHighlight={setMagneticWallHighlight}
              magneticWallHighlight={magneticWallHighlight}
            />
          </PostLayout.MagneticWall>
          {post.publishedAt && (
            <PostLayout.Below>
              <ControlBarContributorsButton post={post} interactionsDisabled>
                {post.archivedAt ? (
                  <>
                    Archived <Timestamp date={post.archivedAt} />
                  </>
                ) : (
                  <>
                    Published <Timestamp date={post.publishedAt} />
                  </>
                )}
              </ControlBarContributorsButton>
            </PostLayout.Below>
          )}
        </PostLayout.Root>
      </PostLayoutProvider>
    </VersionChanges.Root>
  );
};

const NewPostHistoryView = (props: NewPostHistoryViewProps) => {
  return (
    <Page topbar={<InternalTopbar />}>
      <QuillContext>
        <InnerNewPostHistoryView {...props} />
      </QuillContext>
    </Page>
  );
};

export default NewPostHistoryView;
